import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { Flame } from "lucide-react";
import { money, CatBox, CatImage, CreateQuoteModal, OrangeBar, barSelect, barBtn } from "./catalogShared";

export default function SaunaSearch({ onExit, onNav }) {
  const [saunas, setSaunas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [style, setStyle] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [quote, setQuote] = useState(null);
  const [opts, setOpts] = useState({});

  useEffect(() => { api("GET", "/saunas/catalogue").then((r) => setSaunas(r || [])).catch(() => setSaunas([])).finally(() => setLoading(false)); api("GET", "/catalogue/filter-options").then((r) => setOpts(r || {})).catch(() => setOpts({})); }, []);

  const types = useMemo(() => Array.from(new Set(saunas.map((s) => s.sauna_type).filter(Boolean))).sort(), [saunas]);
  const styles = useMemo(() => Array.from(new Set(saunas.map((s) => s.style).filter(Boolean))).sort(), [saunas]);

  const shown = useMemo(() => saunas.filter((s) => {
    if (type && s.sauna_type !== type) return false;
    if (style && s.style !== style) return false;
    const p = Number(s.assembled_price || 0);
    if (minPrice && p < Number(minPrice)) return false;
    if (maxPrice && p > Number(maxPrice)) return false;
    return true;
  }), [saunas, type, style, minPrice, maxPrice]);

  const goStock = () => onNav && onNav("tab:inventory");

  const PriceRow = ({ label, value, item }) => (
    <div className="grid grid-cols-[150px_120px_auto] items-center gap-2 mb-1.5">
      <span className="text-[12px] font-semibold text-slate-800 text-right">{label}:</span>
      <span className="px-2 py-1 border border-[#b9b9b9] bg-white text-[13px] font-black text-[#123a63] text-center">{money(value)}</span>
      {item ? <button onClick={() => setQuote({ title: item.title, item: { name: item.name, price: value, product_id: item.product_id } })} data-testid={item.testid} className="rounded-sm bg-[#4a86d8] text-white font-black text-[12px] px-3 py-1.5 hover:bg-[#2a6cb5]">Create Quotation</button> : <span />}
    </div>
  );

  return (
    <div data-testid="sauna-search-panel" className="rounded-xl overflow-hidden bg-white border border-[#c3d0e6]">
      <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-1.5">
        <Flame className="h-4 w-4 text-white" /><span className="text-white font-black text-sm">Sauna Search</span>
      </div>

      <OrangeBar>
        <label className="flex items-center gap-2 text-[12px] font-black text-white">Sauna Type
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="sauna-f-type" className={barSelect}><option value="">All</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </label>
        <label className="flex items-center gap-2 text-[12px] font-black text-white">Style
          <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="sauna-f-style" className={barSelect}><option value="">All</option>{styles.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </label>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-[12px] font-black text-white justify-between">Minimum Price<select value={minPrice} onChange={(e) => setMinPrice(e.target.value)} data-testid="sauna-f-minprice" className={barSelect}><option value="">All</option>{(opts.min_price || []).map((n) => <option key={n} value={n}>{`$${Number(n).toLocaleString("en-AU")}`}</option>)}</select></label>
          <label className="flex items-center gap-2 text-[12px] font-black text-white justify-between">Maximum Price<select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="sauna-f-maxprice" className={barSelect}><option value="">All</option>{(opts.max_price || []).map((n) => <option key={n} value={n}>{`$${Number(n).toLocaleString("en-AU")}`}</option>)}</select></label>
        </div>
        <div className="ml-auto flex flex-col gap-1.5">
          <button onClick={goStock} data-testid="sauna-view-stock" className={barBtn}>View Stock Saunas</button>
          <button onClick={onExit} data-testid="sauna-close-form" className={barBtn}>Close Form</button>
        </div>
      </OrangeBar>

      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2" data-testid="sauna-result-count">{loading ? "Loading saunas…" : `${shown.length} sauna${shown.length === 1 ? "" : "s"}.`}</p>
        <div className="divide-y-2 divide-slate-200">
          {shown.map((s) => (
            <div key={s.id} data-testid="sauna-row" className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1.1fr_200px] gap-3 py-3 items-start">
              <div>
                <CatBox label="Model" value={s.model} />
                <CatBox label="Brand" value={s.brand} />
                <CatBox label="Persons" value={s.persons} />
                <CatBox label="Cabinet" value={s.cabinet} />
                <CatBox label="Shape" value={s.shape} />
                <CatBox label="Style" value={s.style} />
                <CatBox label="Sound" value={s.sound} />
              </div>
              <div>
                <CatBox label="Width" value={s.width} />
                <CatBox label="Depth" value={s.depth} />
                <CatBox label="Height" value={s.height} />
                <CatBox label="KW" value={s.kw} />
                <CatBox label="Amps" value={s.amps} />
                <CatBox label="Heater Type" value={s.heater_type} labelW={96} />
              </div>
              <div className="pt-1">
                <PriceRow label="RRP" value={s.rrp} />
                <PriceRow label="Discounted Kit Price" value={s.kit_price} item={{ title: `${s.model} (Kit)`, name: `Sauna ${s.model} — Discounted Kit`, price: s.kit_price, product_id: s.id, testid: `sauna-quote-kit-${s.id}` }} />
                <PriceRow label="Assembled Price" value={s.assembled_price} item={{ title: `${s.model} (Assembled)`, name: `Sauna ${s.model} — Assembled`, price: s.assembled_price, product_id: s.id, testid: `sauna-quote-assembled-${s.id}` }} />
              </div>
              <CatImage url={s.image_url} alt={s.model} className="aspect-[4/3] w-full border border-[#9db8d6] rounded-sm" />
            </div>
          ))}
          {!loading && shown.length === 0 && <p className="text-center text-slate-500 text-sm py-8">No saunas match your filters.</p>}
        </div>
      </div>

      {quote && <CreateQuoteModal title={quote.title} item={quote.item} onClose={() => setQuote(null)} />}
    </div>
  );
}
